// BR-37 — the pure half of the screen where an administrator gives people access.
//
// The backend (BR-34, `mantis/src/services/access/`) is the authority: it refuses anything that
// would escalate, and nothing here can make it accept something it would not. What this file
// decides is what the screen OFFERS — only roles and scopes the caller may actually grant — because
// a picker that lets someone build an assignment the server then refuses reads as a broken screen.
//
// So the arithmetic below mirrors `scope.js`'s `containsScope` and `grants.js`'s
// `authorizeAssignments`, on the serialised shapes the API answers with:
//
//   effective scope (what `/access/me` gives per permission)
//     { all: true }  or  { all: false, clients: [{ code, whole, regions, offices }] }
//   assignment scope (what an assignment stores, and what the screen sends)
//     { all: true }  or  { all: false, clients: [{ code, regions?, offices? }] }
//
// An assignment is grantable when its scope is inside the caller's `access.users.manage` scope AND
// inside the caller's scope for every permission of its role. Containment is about the future too:
// a list of offices never contains a region, and a region never contains a whole client, however
// many offices they share today, because the region and the client grow when an office is
// registered. An office is inside a region grant only if its own `region` says so.
//
// Containment of a scope is the containment of each of its parts, so the picker filters part by
// part — every client, region and office it shows is one the caller may grant — and an assignment
// built only from offered parts is one the backend accepts.

import { CODIGOS_DE_PERMISO, ROLES_PREDEFINIDOS, nombreDeRol } from "./accesoDiccionario";

export const USUARIOS = "access.users.manage";
export const ROLES = "access.roles.manage";

const VACIO = Object.freeze({ all: false, clients: [] });

// --- who is asking ------------------------------------------------------------------------------

// Has this business been given the access model? `npm run access:seed` writes an assignment for
// every active user before anything else happens, and no other tenant has any, so "the person
// asking holds one" is the signal — not the mode, which stays `off` while the administrator
// configures people (BR-41 configures first, then shadow, then enforced). A Mantis staff session or
// an API key is never a person with assignments, and gets today's screen.
export const accesoConfigurado = (me) =>
  Boolean(me) && me.source === "assignments" && Array.isArray(me.assignments) && me.assignments.length > 0;

export const alcanceDe = (me, permiso) => (me && me.scopes && me.scopes[permiso]) || VACIO;

const esVacio = (alcance) => !alcance || (!alcance.all && (alcance.clients || []).length === 0);

export const puedeAdministrarUsuarios = (me) => !esVacio(alcanceDe(me, USUARIOS));

// A custom role is a business-wide object, so the backend writes one only for someone holding
// `access.roles.manage` on every client (`grants.requireRolesManage`).
export const puedeAdministrarRoles = (me) => alcanceDe(me, ROLES).all === true;

// May this permission be ticked in a custom role? Only by someone holding it on every client
// (`grants.authorizeRoleWrite`) — a role is business-wide, so it would otherwise hand out on every
// client what its author holds on one. Keeping a permission the role already has is not granting
// it, so an administrator without `cierres.export` can still rename a role that has it.
export const permisoIncluible = (me, permiso, actuales = []) =>
  alcanceDe(me, permiso).all === true || (actuales || []).includes(permiso);

// --- editing a role's permissions ---------------------------------------------------------------

// May the caller change this role's permissions? Administrador never (`locked`: it always holds
// everything, so a business can never take away the last way to manage roles and users); any other
// role, built-in or the business's own, under the custom roles' rule.
export const rolEditable = (me, rol) =>
  Boolean(rol) && !rol.locked && puedeAdministrarRoles(me) && !tieneElRol(me, rol);

// Nobody changes the permissions of a role they hold — that is editing their own access, which the
// backend refuses (`ACCESS_ROLE_SELF_EDIT`) the way it refuses editing one's own assignments.
export const tieneElRol = (me, rol) =>
  Boolean(rol) && ((me && me.assignments) || []).some((a) => String(a.role) === String(rol.id));

// What a role gains and loses going from `antes` to `despues`, in catalogue order — what the save
// confirmation and the "changed for this business" line both say.
export const cambiosDeRol = (antes = [], despues = []) => ({
  gana: CODIGOS_DE_PERMISO.filter((c) => (despues || []).includes(c) && !(antes || []).includes(c)),
  pierde: CODIGOS_DE_PERMISO.filter((c) => (antes || []).includes(c) && !(despues || []).includes(c)),
});

// How many edits a draft holds against the role as saved: each permission switched, plus the name
// and the description of a custom role. A new role counts as changed as soon as anything is typed.
export const cambiosPendientesDeRol = (rol, borrador) => {
  if (!borrador) return 0;
  const { gana, pierde } = cambiosDeRol(rol ? rol.permissions : [], borrador.permissions);
  let total = gana.length + pierde.length;
  if (!rol || !rol.builtIn) {
    if ((borrador.name || "").trim() !== ((rol && rol.name) || "")) total += 1;
    if ((borrador.description || "").trim() !== ((rol && rol.description) || "")) total += 1;
  }
  return total;
};

// --- containment --------------------------------------------------------------------------------

// One part of a scope, which is all the picker ever asks about:
//   { tipo: "todos" }                              every client, present and future
//   { tipo: "cliente", cliente }                   the whole client, future offices too
//   { tipo: "region", cliente, region }            the region, future offices too
//   { tipo: "oficina", cliente, oficina }          exactly that office
//
// `regionDe(cliente, oficina)` answers an office's region: a string, `null` for an office with
// none, `undefined` for one nobody listed. Only an office is ever asked about, and one that cannot
// be placed is outside a region grant — never inside.
export function incluye(efectivo, parte, regionDe = () => undefined) {
  if (!efectivo) return false;
  if (efectivo.all) return true;
  if (parte.tipo === "todos") return false;

  const entrada = (efectivo.clients || []).find((c) => c.code === parte.cliente);
  if (!entrada) return false;
  if (entrada.whole) return true;
  if (parte.tipo === "cliente") return false;

  const regiones = entrada.regions || [];
  if (parte.tipo === "region") return regiones.includes(parte.region);

  if ((entrada.offices || []).includes(parte.oficina)) return true;
  const region = regionDe(parte.cliente, parte.oficina);
  return typeof region === "string" && regiones.includes(region);
}

// Every scope a grant of `rol` has to fit inside: the caller's user-management scope, and theirs
// for each permission the role carries.
const limitesDe = (me, rol) => [
  alcanceDe(me, USUARIOS),
  ...((rol && rol.permissions) || []).map((permiso) => alcanceDe(me, permiso)),
];

export const permite = (me, rol, parte, regionDe) =>
  Boolean(rol) && limitesDe(me, rol).every((limite) => incluye(limite, parte, regionDe));

// "Todos los clientes" — never for a client-bound role (Cliente), which names exactly one.
export const todosOtorgable = (me, rol) =>
  Boolean(rol) && !rol.clientBound && permite(me, rol, { tipo: "todos" });

const alcanzaCliente = (limite, codigo) =>
  limite.all || (limite.clients || []).some((c) => c.code === codigo);

// The clients a grant of `rol` can reach at all — those every limit names. Which of their regions
// and offices are grantable needs the offices (`opcionesDeCliente`).
export const clientesOtorgables = (me, rol, clientes) => {
  if (!rol) return [];
  const limites = limitesDe(me, rol);
  return (clientes || []).filter((c) => limites.every((limite) => alcanzaCliente(limite, c.code)));
};

// The roles worth offering: those the caller can grant somewhere. A role they cannot grant
// anywhere would only ever earn a 403, so it is not in the list. `actual` keeps a role an existing
// assignment already holds, so the editor never shows an assignment whose role vanished from its
// own picker.
export const rolesOtorgables = (me, roles, clientes, actual = null) =>
  (roles || []).filter(
    (rol) => rol.id === actual || todosOtorgable(me, rol) || clientesOtorgables(me, rol, clientes).length > 0
  );

// What of one client the picker may offer for `rol`: the whole client, and which of its regions
// and offices. `oficinas` is `listCierreOffices`' answer for that client.
export function opcionesDeCliente(me, rol, cliente, oficinas, regionDe) {
  const lista = oficinas || [];
  const regiones = [...new Set(lista.map((o) => o.region).filter((r) => typeof r === "string" && r))].sort((a, b) =>
    a.localeCompare(b, "es")
  );
  return {
    entero: permite(me, rol, { tipo: "cliente", cliente }),
    regiones: regiones.filter((region) => permite(me, rol, { tipo: "region", cliente, region })),
    oficinas: lista.filter((o) => permite(me, rol, { tipo: "oficina", cliente, oficina: o.officeCode }, regionDe)),
  };
}

// `regionDe` from the offices the screen has loaded, keyed by client: `{ [clientCode]: offices }`.
export function mapaDeRegiones(oficinasPorCliente) {
  const regiones = new Map();
  Object.entries(oficinasPorCliente || {}).forEach(([cliente, oficinas]) => {
    (oficinas || []).forEach((o) => regiones.set(`${cliente}\u0000${o.officeCode}`, o.region || null));
  });
  return (cliente, oficina) => {
    const clave = `${cliente}\u0000${oficina}`;
    return regiones.has(clave) ? regiones.get(clave) : undefined;
  };
}

// --- the editor's draft -------------------------------------------------------------------------

// A draft is what the editor holds while a person works on one assignment: the role and, per
// chosen client, whether it is the whole client, some regions or some offices. A client is
// narrowed to regions OR offices, never both — the backend refuses "Antioquia plus 3091" in one
// entry, which is two assignments a person can see as two.
let siguienteClave = 0;
const nuevaClave = () => {
  siguienteClave += 1;
  return `asignacion-${siguienteClave}`;
};

const modoDe = (entrada) => {
  if ((entrada.offices || []).length > 0) return "oficinas";
  if ((entrada.regions || []).length > 0) return "regiones";
  return "entero";
};

export const borradorNuevo = (role = "") => ({
  clave: nuevaClave(),
  role,
  scope: { all: false, clients: [] },
});

export const aBorrador = (asignacion) => ({
  clave: nuevaClave(),
  role: asignacion.role,
  scope:
    asignacion.scope && asignacion.scope.all
      ? { all: true, clients: [] }
      : {
          all: false,
          clients: ((asignacion.scope && asignacion.scope.clients) || []).map((c) => ({
            code: c.code,
            modo: modoDe(c),
            regions: [...(c.regions || [])],
            offices: [...(c.offices || [])],
          })),
        },
});

// What `users/assignments` takes: only the half of a client entry its mode uses, so switching from
// offices to regions and back does not smuggle the other list along.
export const aAsignacionDelServidor = (borrador) => ({
  role: borrador.role,
  scope: borrador.scope.all
    ? { all: true }
    : {
        all: false,
        clients: borrador.scope.clients.map((c) => {
          if (c.modo === "regiones") return { code: c.code, regions: [...c.regions] };
          if (c.modo === "oficinas") return { code: c.code, offices: [...c.offices] };
          return { code: c.code };
        }),
      },
});

// The parts a draft names, for checking each against what the caller may grant.
export const partesDeBorrador = (borrador) => {
  if (borrador.scope.all) return [{ tipo: "todos" }];
  return borrador.scope.clients.flatMap((c) => {
    if (c.modo === "regiones") return c.regions.map((region) => ({ tipo: "region", cliente: c.code, region }));
    if (c.modo === "oficinas") return c.offices.map((oficina) => ({ tipo: "oficina", cliente: c.code, oficina }));
    return [{ tipo: "cliente", cliente: c.code }];
  });
};

const nombreDeParte = (parte, nombreDeCliente) => {
  if (parte.tipo === "todos") return "todos los clientes";
  const cliente = nombreDeCliente(parte.cliente);
  if (parte.tipo === "cliente") return cliente;
  if (parte.tipo === "region") return `la región ${parte.region} de ${cliente}`;
  return `la oficina ${parte.oficina} de ${cliente}`;
};

// What stops one draft from being saved, in words, before the server is asked. Empty when it
// may be sent. The same checks the backend makes, in the same order, so the screen and the 400 or
// 403 never disagree — the server's answer is still shown verbatim if they ever do.
export function problemasDeBorrador(borrador, { me, roles, regionDe, nombreDeCliente = (c) => c }) {
  const rol = (roles || []).find((r) => r.id === borrador.role);
  if (!borrador.role) return ["Elige un rol."];
  if (!rol) return [`El rol ${borrador.role} ya no existe; elige otro.`];

  const problemas = [];
  const { scope } = borrador;
  if (!scope.all && scope.clients.length === 0) {
    problemas.push("Elige todos los clientes o al menos uno.");
  }
  if (rol.clientBound && (scope.all || scope.clients.length !== 1)) {
    problemas.push(`${nombreDeRol(rol.id, roles)} debe limitarse a exactamente un cliente.`);
  }
  if (!scope.all) {
    scope.clients.forEach((c) => {
      if (c.modo === "regiones" && c.regions.length === 0) {
        problemas.push(`Elige al menos una región de ${nombreDeCliente(c.code)}.`);
      }
      if (c.modo === "oficinas" && c.offices.length === 0) {
        problemas.push(`Elige al menos una oficina de ${nombreDeCliente(c.code)}.`);
      }
    });
  }

  const fuera = partesDeBorrador(borrador).filter((parte) => !permite(me, rol, parte, regionDe));
  if (fuera.length > 0) {
    problemas.push(
      `No puedes otorgar ${nombreDeRol(rol.id, roles)} sobre ${fuera
        .map((parte) => nombreDeParte(parte, nombreDeCliente))
        .join(", ")}.`
    );
  }
  return problemas;
}

// --- saying it ----------------------------------------------------------------------------------

export const nombreDeUsuario = (usuario) =>
  [usuario?.firstName, usuario?.lastName].filter(Boolean).join(" ").trim() || usuario?.email || "Usuario";

// --- the Usuarios list, in groups ---------------------------------------------------------------

// Alphabetical the way a person reads a name: "álvaro" beside "Alberto", not after "Zoe".
const compararNombres = (a, b) => nombreDeUsuario(a).localeCompare(nombreDeUsuario(b), "es", { sensitivity: "base" });

// The caller first, then everyone else by their highest role — the built-in ones from most to least
// access, then the business's own in the order the roles list gives them — and last whoever holds no
// role that resolves. Alphabetical inside each group. Someone holding two roles sits under the
// higher one; their row still lists both. Empty groups are left out, so a search shows only the
// groups it matched.
export function gruposDeUsuarios(usuarios = [], roles = []) {
  const predefinidos = Object.keys(ROLES_PREDEFINIDOS);
  const propios = (roles || []).filter((r) => !r.builtIn).map((r) => r.id);
  const orden = [...predefinidos, ...propios];
  const rango = (usuario) => {
    const rangos = (usuario.assignments || []).map((a) => orden.indexOf(String(a.role))).filter((i) => i >= 0);
    return rangos.length > 0 ? Math.min(...rangos) : orden.length;
  };

  const grupos = [];
  const propio = (usuarios || []).filter((u) => u.self);
  if (propio.length > 0) grupos.push({ clave: "tu", titulo: "Tú", usuarios: propio });

  const resto = (usuarios || []).filter((u) => !u.self);
  orden.forEach((clave, i) => {
    const delRol = resto.filter((u) => rango(u) === i).sort(compararNombres);
    if (delRol.length > 0) grupos.push({ clave, titulo: nombreDeRol(clave, roles), usuarios: delRol });
  });
  const sinAcceso = resto.filter((u) => rango(u) === orden.length).sort(compararNombres);
  if (sinAcceso.length > 0) grupos.push({ clave: "sin-acceso", titulo: "Sin acceso", usuarios: sinAcceso });
  return grupos;
}

const lista = (valores) => [...valores].sort((a, b) => String(a).localeCompare(String(b), "es")).join(", ");

// An assignment scope in one line: "Todos los clientes", "Davivienda", "Davivienda · regiones
// Antioquia", "Davivienda · oficinas 3091, 4956".
export function describirAlcance(scope, nombreDeCliente = (c) => c) {
  if (!scope || scope.all) return "Todos los clientes";
  const clientes = scope.clients || [];
  if (clientes.length === 0) return "Ningún cliente";
  return clientes
    .map((c) => {
      const nombre = nombreDeCliente(c.code);
      if ((c.offices || []).length > 0) {
        return `${nombre} · ${c.offices.length === 1 ? "oficina" : "oficinas"} ${lista(c.offices)}`;
      }
      if ((c.regions || []).length > 0) {
        return `${nombre} · ${c.regions.length === 1 ? "región" : "regiones"} ${lista(c.regions)}`;
      }
      return nombre;
    })
    .join("; ");
}

export const describirAsignacion = (asignacion, roles, nombreDeCliente) =>
  `${nombreDeRol(asignacion.role, roles)} — ${describirAlcance(asignacion.scope, nombreDeCliente)}`;

// --- history ------------------------------------------------------------------------------------

const claveDeAlcance = (scope) => {
  if (!scope || scope.all) return "*";
  return [...(scope.clients || [])]
    .map((c) => `${c.code}:${[...(c.regions || [])].sort().join(",")}:${[...(c.offices || [])].sort().join(",")}`)
    .sort()
    .join("|");
};

const claveDeAsignacion = (a) => `${a.role}\u0000${claveDeAlcance(a.scope)}`;

// One change-log entry as what was added and what was taken away. An assignment that survived the
// change unchanged is neither; the backend keeps its grantor and date, and so does this.
export function cambiosDeEntrada(entrada) {
  const antes = (entrada && entrada.before) || [];
  const despues = (entrada && entrada.after) || [];
  const clavesAntes = new Set(antes.map(claveDeAsignacion));
  const clavesDespues = new Set(despues.map(claveDeAsignacion));
  return {
    agregadas: despues.filter((a) => !clavesAntes.has(claveDeAsignacion(a))),
    quitadas: antes.filter((a) => !clavesDespues.has(claveDeAsignacion(a))),
  };
}

// Who made a change, as `AccessChangeLog.actor` stores it: `business_user` (someone on this
// screen), `staff` (Mantis, from the Admin app — BR-40) or `script` (`npm run access:seed`).
export const autorDeEntrada = (entrada) => {
  const actor = entrada && entrada.actor;
  if (!actor) return "Desconocido";
  const etiqueta = actor.label || actor.id || "";
  if (actor.kind === "script") return `Carga inicial${etiqueta ? ` (${etiqueta})` : ""}`;
  if (actor.kind === "staff") return `Mantis${etiqueta ? ` (${etiqueta})` : ""}`;
  return etiqueta || "Desconocido";
};
